/**
 * Route guard for the org-intake pending-clinic queue (ORG-INTAKE-QUEUE-V1,
 * B2 / Story 5.4; initially configured for COC in the Partha workflow).
 * Mirrors RequireLeadCockpit: the pure decision lives in
 * decideOrgIntakeAccess (org-intake-view.ts) and admits the ROUTABLE role
 * superset -- the queue's real owner role is CONFIG-RESOLVED server-side
 * (org-default new_lead_followup routing_config; default 'coc'), so this
 * guard deliberately does NOT hardcode the working role. NO /me flag, NO
 * feature flag, NO new nav section (internal surface, default-on per
 * ADR-0016).
 *
 * Backend route auth (the owner-config gate on GET /api/org-intake/queue
 * and POST /api/leads/:id/set-clinic) remains the real security boundary;
 * a routable role that is not the configured owner gets the endpoint's
 * 401, which the page renders as the honest access-restricted state.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { decideOrgIntakeAccess } from '@/dashboard/org-intake/org-intake-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireOrgIntakeViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  /** Rendered when access is granted. Defaults to `<Outlet />`. */
  readonly children?: React.ReactNode;
}

/** Pure presentational view - testable via react-dom/server. */
export function RequireOrgIntakeView({
  loading,
  error,
  me,
  children,
}: RequireOrgIntakeViewProps): JSX.Element | null {
  const decision = decideOrgIntakeAccess({ loading, error, me });
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

export default function RequireOrgIntake(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  if (!ctx) {
    return <Navigate to="/login" replace />;
  }
  return (
    <RequireOrgIntakeView loading={ctx.loading} error={ctx.error} me={ctx.me} />
  );
}
